<script>
  import BlockGrid from '#lib/BlockGrid.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Image from '#lib/Image.svelte';
  import inview from '#lib/inview.js';
  import { getContext } from 'svelte';
  import {
    generate_css_variables,
    render_micro_markdown,
    localize_utc_date,
    is_external_url,
  } from '#lib/util.js';

  let { is_editing } = $props();

  let site_settings = getContext('site_settings');
  let entry_api = getContext("entry_api");
  let _is_editing = $derived(is_editing || entry_api?.is_editing);

  const defined_nav_sections = $derived([1, 2, 3, 4].filter(i => 
    site_settings[`footer_nav_${i}_title`] || 
    site_settings[`footer_nav_${i}_items`]?.length > 0 || 
    site_settings[`footer_nav_${i}_description`]
  ));
        
  const nav_sections_half_length = $derived(Math.ceil(defined_nav_sections.length / 2));

  /*
    Safari paints everything outside the document - the rubber-band overscroll and, on iOS 26, the
    strip behind the floating toolbar at the end of the page - with one color it takes from the page:
    body's background-color over html's (WebKit's `documentBackgroundColor`, background images
    ignored). The page cannot paint there itself - a pseudo-element hanging below the footer only
    makes the page longer - so below a colored footer that strip stays the page color.

    So once the reader is nearer the end of the page than its start, body's background-color becomes
    the footer color, while a background-image of the page color keeps the page itself looking the
    same. Nearer the start it stays the page color for the overscroll above the header, and pages that
    do not scroll keep it throughout.
  */
  $effect(() => {
    const footer_color = site_settings.footer_color;
    if (!site_settings.footer_visible || !footer_color || footer_color === 'transparent') return;

    const body = document.body;
    let is_past_middle;

    function update() {
      const max_scroll = document.documentElement.scrollHeight - window.innerHeight;
      const past_middle = max_scroll > 0 && window.scrollY > max_scroll / 2;
      if (past_middle === is_past_middle) return;
      is_past_middle = past_middle;
      if (past_middle) {
        body.style.backgroundColor = `var(--color-${footer_color.replace(/_/g, '-')}-background)`;
        body.style.backgroundImage = 'linear-gradient(var(--color-default-background) 0 0)';
      } else {
        body.style.removeProperty('background-color');
        body.style.removeProperty('background-image');
      }
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      body.style.removeProperty('background-color');
      body.style.removeProperty('background-image');
    };
  });
</script>

{#snippet footer_menu(section_index, nav_items, title, description)}
  {#if title || nav_items.length > 0 || description}
    <div class='menu-section menu-section-{section_index}'>
      {#if title}
        <p class='menu-section-title text-preamble text-2 leading-trim'>
          {title}
        </p>
      {/if}
      {#if description}
        <p class='menu-section-description text-control text-2 leading-trim'>
          {@html render_micro_markdown(description)}
        </p>
      {/if}
      {#if nav_items.length > 0}
        <nav class='menu-section-items'>
          {#each nav_items as { label, href, show_label_with_image, image, type }, i}
            <a 
              {href}
              target={is_external_url(href) ? '_blank' : undefined}
              rel={is_external_url(href) ? 'noopener noreferrer' : undefined}
            >
              {#snippet nav_item_image()}
                <Image 
                  alt={label} 
                  {...image} 
                  style="width: auto; max-height: var(--max-dynamic-island-image-height); min-height: var(--min-dynamic-island-image-height);"/>
              {/snippet}
              <!-- Show label when there's no image, otherwise only when explicitly included -->
              <ButtonLabel 
                variant='brand flush'
                start_icon={image ? nav_item_image : undefined}
                text={!image || (image && show_label_with_image) ? label : undefined}
                align='start'
              />
            </a>
          {/each}
        </nav>
      {/if}
    </div>
  {/if}
{/snippet}

{#if site_settings.footer_visible}
  <BlockGrid class='footer-container'>
    <footer 
      style='{generate_css_variables(site_settings.footer_color)}'
      class='footer-variant-{site_settings.footer_layout}' 
      class:blocking-overlay={is_editing}
    >
      <div 
        class='menu-sections menu-sections-count-{defined_nav_sections.length}'
      >
        <div class='menu-sub-group menu-sub-group-1'>
          {#each defined_nav_sections.slice(0, nav_sections_half_length) as groupIndex}
            {@render footer_menu(
              groupIndex,
              site_settings[`footer_nav_${groupIndex}_items`],
              site_settings[`footer_nav_${groupIndex}_title`],
              site_settings[`footer_nav_${groupIndex}_description`]
            )}
          {/each}
        </div>
        
        {#if defined_nav_sections.length > 1}
          <div class='menu-sub-group menu-sub-group-2'>
            {#each defined_nav_sections.slice(nav_sections_half_length) as groupIndex}
              {@render footer_menu(
                groupIndex,
                site_settings[`footer_nav_${groupIndex}_items`],
                site_settings[`footer_nav_${groupIndex}_title`],
                site_settings[`footer_nav_${groupIndex}_description`]
              )}
            {/each}
          </div>
        {/if}
      </div>
      {#if site_settings.footer_image}
        <Image class='footer-image' {...site_settings.footer_image} />
      {/if}
      <div class='legal'>
        {#if site_settings.footer_outro}
          <p class='outro text-preamble leading-trim'>{@html render_micro_markdown(site_settings.footer_outro || '')}</p>
        {/if}
        <p class='copyright text-preamble leading-trim'>&copy; { localize_utc_date(new Date()).getUTCFullYear() } {site_settings.legal_name}</p>
      </div>
      {@render footer_menu(
        5,
        site_settings.footer_nav_5_items, 
        site_settings.footer_nav_5_title, 
        site_settings.footer_nav_5_description
      )}
    </footer>
  </BlockGrid>
{/if}

<style lang='postcss'>
  /* Register animatable color properties */
  @property --footer-bg {
    syntax: '<color>';
    inherits: true;
    initial-value: transparent;
  }
  
  @property --footer-fg {
    syntax: '<color>';
    inherits: true;
    initial-value: currentcolor;
  }

  :root {
    --footer-margin-top: calc(2 * var(--block-spacing-l));
  }

  :global(*:has(+ .footer-container)) {
    padding-block-end: var(--footer-margin-top) !important;
  }
  footer {
    --color: var(--color-foreground);
    position: relative;
    background-color: var(--color-background);
    display: grid;
    grid-template-columns: inherit;
    grid-column: 1 / -1;
    padding-block: var(--block-spacing-m);
    row-gap: var(--block-spacing-l);

    --transition-duration: 0.5s;
    --transition-timing-function: var(--smooth-ease-out);

    :global(.footer-image) {
      width: 100%;
      max-width: 100%;
      /* the image keeps its intrinsic height attribute otherwise (leaving big gaps when scaled down) */
      height: auto;
    }

    color: var(--color-foreground);
    :global(*) { 
      color: inherit !important; 
    }

    .legal {
      display: flex;
      flex-direction: column;
      gap: var(--size-4);
      justify-content: center;
      /* One line of fine print, not running text: it may take the column's whole width rather
         than the preamble's reading measure, which broke a longer company name onto two lines */
      .copyright {
        max-width: none;
        /* Where the column is too narrow, it breaks between words, evenly */
        hyphens: manual;
        text-wrap: balance;
      }
    }

    :global(.button-label) {
      padding-inline: 0 !important;
      min-height: 0;
    }
    :global(.menu-section-description) {
      padding-block: var(--input-block-spacing);
    }
    .menu-sections {
      row-gap: var(--block-spacing-l);
      :global(.menu-sub-group) {
        display: contents;
      }
      :global(.menu-section) {
        display: flex;
        flex-direction: column;
        gap: var(--block-spacing-s);
        min-width: 0; /* Allow shrinking below content size */
        :global(.menu-section-items) {
          display: flex;
          flex-direction: column;
          min-width: 0;
          :global(.button-label) {
            justify-content: start;
          }
          :global(a) {
            min-width: 0;
            max-width: 100%;
          }
          :global(.button-text) {
            overflow-wrap: break-word;
            word-break: break-word;
          }
        }
      }
    }
    :global(.menu-section-5) {
      gap: var(--block-spacing-xs);
    }

    /* Inner Track */
    &.footer-variant-1,
    &.footer-variant-5 {
      --column-1-start: 14;
      --column-2-end: 38;
    }
    /* Extended 1 Track */
    &.footer-variant-2,
    &.footer-variant-6,
    &.footer-variant-13 {
      --column-1-start: 10;
      --column-2-end: 42;
    }
    /* Extended 2 Track */
    &.footer-variant-3,
    &.footer-variant-7 {
      --column-1-start: 6;
      --column-2-end: 46;
    }
    /* Flex Track */
    &.footer-variant-4,
    &.footer-variant-8 {
      --column-1-start: 2;
      --column-2-end: 50;
    }
    /* Inner Track */
    &.footer-variant-1 :global(.footer-image) {
      grid-area: 2 / 14 / 3 / 38;
    }
    /* Extended 1 & 2 Track */
    &.footer-variant-2 :global(.footer-image),
    &.footer-variant-3 :global(.footer-image) {
      grid-area: 2 / 10 / 3 / 42;
    }
    /* Flex Track */
    &.footer-variant-4 :global(.footer-image) {
      grid-area: 2 / 6 / 3 / 46;
    }
    &.footer-variant-1,
    &.footer-variant-2,
    &.footer-variant-3,
    &.footer-variant-4,
    &.footer-variant-13 {
      --column-1-end: 25;
      --column-2-start: 27;
      grid-template-rows: auto auto auto;
      :global(.footer-image) {
        max-width: 100%;
        :global(&:not(.svg)){
          /* don't scale up raster images */
          object-fit: scale-down;
          width: 100%;
        }
      }
      .menu-sections {
        grid-area: 1 / var(--column-1-start) / 2 / var(--column-2-end);
        display: grid;
        grid-template-columns: 1fr;
        @media (min-width: 380px) {
          grid-template-columns: 1fr 1fr;
        }
        @media (min-width: 769px) {
          grid-template-columns: 1fr 1fr 1fr 1fr;
          :global(.menu-section) {
            &:not(:first-child) {
              margin-inline: var(--grid-gutter);
            }
          }           
        }
        @media (min-width: 1025px) {
          :global(.menu-section-3) {
            justify-self: center;
          }
          :global(.menu-section-4) {
            justify-self: flex-end;
          }
          &:has(.menu-section:nth-child(4)) {
            grid-template-columns: 50% 1fr 1fr 1fr;
          }
        }
      }
      .legal {
        grid-area: 4 / var(--column-1-start) / 5 / var(--column-2-end);
        @media (min-width: 769px) {
          grid-area: 3 / var(--column-1-start) / 4 / var(--column-1-end);
        }
      }
      :global(.menu-section-1) {
        padding-inline-end: var(--grid-gutter);
        @media (min-width: 769px) {
          padding-inline-end: 33%;
        }
      }
      :global(.menu-section-5) {
        grid-template-columns: 1fr;
        grid-area: 3 / var(--column-1-start) / 4 / var(--column-2-end);
        @media (min-width: 769px) {
          grid-template-columns: 1fr auto;
          grid-area: 3 / var(--column-2-start) / 4 / var(--column-2-end);
          justify-items: flex-end;
        }
        display: grid;
        /* The description's row is created only when it is placed there: an empty second row
           still got the row gap, so the links sat high beside the legal line */
        grid-template-rows: auto;
        align-items: center;
        column-gap: var(--size-10);
        row-gap: var(--grid-gutter);
        @media (min-width: 769px) {
          :global(.menu-section-title) {
            grid-column: 1 / 2;
            grid-row: 1 / 2;
          }
          :global(.menu-section-description) {
            grid-column: 1 / 3;
            grid-row: 2 / 3;
          }
        }
        :global(.menu-section-items) {
          /* A few short links (privacy policy, imprint): one row spread across the column, wrapping
             only when they do not fit - not a list stacked down a phone screen */
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-start;
          /* The links are `flush` labels - no padding of their own - so in a row the gap has
             to come from here, as it does for this section in variants 5-8 */
          column-gap: var(--grid-gutter);
          @media (min-width: 769px) {
            grid-column: 2 / 3;
            grid-row: 1 / 2;
            justify-content: flex-end;
          }
        }
      }
    }

    /* ————————————————————————————————————————————— 13 —————————————————————————————————————————————————————————— */
    /* Variant of layout 2 (Extended 1 Track) with the footer image on top and menu sections below */
    &.footer-variant-13 {
      :global(.footer-image) {
        grid-area: 1 / var(--column-1-start) / 2 / var(--column-2-end);
      }
      .menu-sections {
        grid-area: 2 / var(--column-1-start) / 3 / var(--column-2-end);
      }
    }

    &:is(.footer-variant-5, .footer-variant-6, .footer-variant-7, .footer-variant-8) {
      --column-1-end: 25;
      --column-2-start: 27;
      @media (max-width: 768px) {
        row-gap: var(--block-spacing-m);
      }
      :global(.footer-image) {
        grid-area: 1 / var(--column-1-start) / 2 / var(--column-2-end);
        @media (min-width: 1025px) {
          grid-area: 1 / var(--column-1-start) / 2 / 21;
        }
      }
      .menu-sections {
        display: grid;
        grid-area: 2 / var(--column-1-start) / 3 / var(--column-2-end);
        /*
          Sections share one row as soon as they genuinely fit, at every width.

          Three things went wrong here before, worth recording because each looked like the fix:

          1. Hard-coding 1 → 2 columns and only reaching `auto-fit` at 1025px meant three active
             menus always broke below that: two in the first row, the third orphaned beneath.
          2. `auto-fit, minmax(14ch, 1fr)` then failed for a subtler reason — `ch` is the width of
             "0" in the *current font*, and in Teodor at 20.5px that is 12.8px, so 14ch resolved to
             179px. Three of those plus two gaps needed 593px inside a 586px container: seven
             pixels short, so it silently dropped to two columns. A `ch`-based minimum is hostage
             to whatever font the site is set in, which is exactly what a shared component cannot
             assume.
          3. Counting sections with `:has(.menu-section:nth-child(n))` cannot work here: the
             sections are wrapped in `.menu-sub-group` elements which are `display: contents`, so
             they *act* as grid items but their `nth-child` index counts within their sub-group.
             `nth-child(3)` never matches however many sections exist.

          So: `rem`, not `ch` — font-independent. 8rem (128px) clears the widest section's
          min-content (~107px here) with room to spare, and lets three or four sections share a row
          in the half-width container this sits in. `min(…, 100%)` keeps a single column from
          overflowing on a narrow screen.
        */
        column-gap: var(--grid-gutter);
        row-gap: var(--block-spacing-s);
        grid-template-columns: repeat(auto-fit, minmax(min(8rem, 100%), 1fr));
        @media (min-width: 1025px) {
          grid-area: 1 / var(--column-2-start) / 2 / var(--column-2-end);
          /* With one or two sections, each pushes to the end of its column. Not with three:
             `:first-child:last-child` matched the lone section of the second sub-group
             (`display: contents`), so the last one was right-aligned and its gap to the others
             widened. */
          &:is(.menu-sections-count-1, .menu-sections-count-2) :global(.menu-section) {
            justify-self: flex-end;
            :global(.menu-section-items) {
              align-items: flex-end;
            }
            :global(.menu-section-title) {
              align-self: flex-end;
            }
            :global(.menu-section-items .button-label) {
              justify-content: flex-end;
            }
          }
        }
      }
      .legal {
        grid-area: 3 / var(--column-1-start) / 4 / var(--column-2-end);
        @media (min-width: 1025px) {
          grid-area: 2 / var(--column-1-start) / 3 / var(--column-1-end);
        }
      }
      :global(.menu-section-5) {
        grid-area: 4 / var(--column-1-start) / 5 / var(--column-2-end);
        justify-content: flex-end;
        display: flex;
        flex-direction: column;
        :global(.menu-section-items) {
          display: flex;
          gap: var(--grid-gutter);
        }
        @media (min-width: 1025px) {
          grid-area: 2 / var(--column-2-start) / 3 / var(--column-2-end);
          :global(.menu-section-items) {
            justify-content: flex-end;
          }
        }
      }
    }



    /* ————————————————————————————————————————————— 9, 10, 11, 12 —————————————————————————————————————————————————————————— */
    /* ——————————————————————————————————————— Variant Center Footer Image —————————————————————————————————————————————————— */
    /* Inner Track */
    &.footer-variant-9 {
      --column-1-start: var(--inner-track-start);
      --column-3-end: var(--inner-track-end);
    } 
    /* Inner Track */
    &.footer-variant-9, 
    /* Extended 1 Track */
    &.footer-variant-10, 
    /* Extended 2 Track */
    &.footer-variant-11, 
    /* Flex Track */
    &.footer-variant-12 {

      /* Variables for column spans */
      --column-1-end: var(--center-gutter-track-start);
      @media (min-width: 641px) {
        --column-1-end: 19;
      }
      --column-2-start: 22;
      --column-2-end: 30;
      --column-3-start: var(--center-gutter-track-end);
      @media (min-width: 641px) {
        --column-3-start: 33;
      }


      /* Footer image */
      :global(.footer-image) {
        grid-column: var(--column-2-start) / var(--column-2-end);
        grid-row: 1 / 3;
        /* align-self: center; */
        @media (max-width: 640px) {
          grid-row: -2 / -1;
          grid-column: var(--column-1-start) / var(--column-3-end);
        }
      }
      :global(.footer-image.svg) {
        @media (max-width: 640px) {
          grid-column: 16 / 36;
        }
      }

      /* Menu sections */
      .menu-sections {
        display: contents;
        :global(.menu-sub-group) {
          display: flex;
          flex-direction: var(--menu-sub-group-direction);
          gap: var(--grid-gutter);
          grid-row: 1 / 2;
          justify-content: space-between;
        }
        :global(.menu-sub-group-1) {
          grid-column: var(--column-1-start) / var(--column-1-end);
        }
        :global(.menu-sub-group-2) {
          grid-column: var(--column-3-start) / var(--column-3-end);
          justify-self: end;
          :global(*){
            text-align: end;
            align-self: end;
          }
        }

        @media (max-width: 640px) {
          &.menu-sections-count-1,
          &.menu-sections-count-2 {
            grid-area: 1 / var(--column-1-start) / 2 / var(--column-3-end);
            display: flex;
            flex-direction: column;
            align-items: center;
            :global(.menu-sub-group) {
              display: contents;
              :global(*) {
                align-self: center;
                text-align: center;
              }
            }
          }
        }
      }

      /* Legal & Menu section 5 */
      .legal {
        grid-row: 2 / 3;
        margin-block-start: var(--block-spacing-m);
      }
      .legal, :global(.menu-section-5) {
        grid-column: var(--column-1-start) / var(--column-3-end);
        align-items: center;
      }
      :global(.menu-section-5) {
        display: flex;
        flex-direction: column;
        grid-row: 3 / 4;
      }
    }

    /* Adjustments for different track layouts */
    /* Inner Track */
    &.footer-variant-9 {
      --menu-sub-group-direction: column;
      :global(.menu-sub-group) {
        justify-content: space-between;
        row-gap: var(--block-spacing-l) !important;
      }
    }
    /* Extended 1 Track */
    &.footer-variant-10 {
      --column-1-start: var(--extended-1-track-start);
      --column-3-end: var(--extended-1-track-end);
    }
    /* Extended 2 Track */
    &.footer-variant-11 {
      --column-1-start: var(--extended-2-track-start);
      --column-3-end: var(--extended-2-track-end);
    }
    /* Flex Track */
    &.footer-variant-12 {
      --column-1-start: var(--flex-track-start);
      --column-3-end: var(--flex-track-end);
    }

    /* Menu sub group direction */
    &:is(.footer-variant-10, .footer-variant-11, .footer-variant-12) {
      --menu-sub-group-direction: column;
      @media (min-width: 1025px) {
        --menu-sub-group-direction: row;
      }
    }
  }
</style>